// import Vue from 'vue';
import "@root/scss/custom.scss";
import { reflectTheme, getStoredTheme, seedThemeFromServer } from '@root/services/theme';
import startLegacyFaIcons from '@root/services/icons/legacyFaIcons';

// Apply the persisted theme onto <html data-theme> BEFORE the app mounts, so
// the token layer resolves the correct palette on first paint (no flash of the
// wrong theme). Light is the default when nothing is stored.
reflectTheme(getStoredTheme());

import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import router from '@apexl/icarus-core/src/router/index.js'
import { activeThemeKeys } from '@root/services/themeComponents';
import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
// Bootstrap MUST be imported from exactly ONE build. Importing the UMD bundle
// AND the ESM build ("bootstrap") registers TWO document-level data-API
// handlers with separate instance maps — on a click of an OPEN submenu toggle
// one copy hides while the other re-shows, so submenus could never be closed.
// The ESM build registers every component's data-API and pulls Popper from
// @popperjs/core itself.
import { Collapse } from "bootstrap";
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
// Onboarding slice 2a — project fork of icarus-core's Icarus.vue: adds the auth
// marketing panel + the client-rendered registration flow. Faithful copy of the
// vendor render loop otherwise.
import Icarus from "@root/components/Icarus.vue";
// F-RN-03 — unknown paths hard-redirect to the static /404.html (see the
// guard near the bottom of this file; an in-app 404 route can't render
// because App.vue has no <router-view>).
import { register as registerBranding } from '@apexl/icarus-branding';
import { register as registerContentFlows } from '@apexl/icarus-content-flows';
import { register as registerAi } from '@apexl/icarus-ai';
import { register as registerPermissions } from '@apexl/icarus-permissions';
import { register as registerContentLibrary } from '@apexl/icarus-content-library';
import { register as registerTaxonomy } from '@apexl/icarus-taxonomy';
import { register as registerViews } from '@apexl/icarus-views';
// Onboarding slice 7 — notification preferences / admin-matrix / tenant-override
// surfaces from apexl/io-notifications' companion package. Must be BOTH
// npm-installed AND register()'d here (the 0de9741 lesson: a package added to
// package.json but never registered contributes no components at build time).
import { register as registerNotifications } from '@apexl/icarus-notifications';
import { defaultConfig as formkitConfig, plugin as formkit } from "@formkit/vue";

const app = createApp(Icarus);

// Defensive: a 3rd-party script throwing during boot (e.g. remote FA kit
// returning 403, analytics script blocked by an ad blocker, etc.) can
// cascade into Vue's runtime error path and prevent the app mounting.
// This handler logs the error but keeps the app alive. Replace with
// proper Sentry/Bugsnag wiring per project as needed.
app.config.errorHandler = (err, instance, info) => {
  // eslint-disable-next-line no-console
  console.error('[Vue errorHandler]', info, err);
};

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(router);

// F-OB-03 — cross-device theme follow. Pinia (and therefore the auth token
// useTokenStore reads) is live as of the line above, so it's safe to kick
// this off now. Deliberately NOT awaited: the synchronous reflectTheme(
// getStoredTheme()) call at the top of this file already painted the
// correct-for-this-device theme before any network round trip could
// resolve (no boot-time FOUC). This only upgrades the theme afterwards if
// the server's on-record preference disagrees with the local cache (e.g.
// first load on a new device) — see icarus/src/services/theme.js for the
// full contract. The endpoint may not exist yet (parallel backend build);
// seedThemeFromServer() swallows a 404 / auth failure / network error
// silently and leaves the local cache exactly as it was.
seedThemeFromServer();

app.component('VueDatePicker', VueDatePicker);

app.use(formkit, formkitConfig({
  theme: 'genesis'
}));

// Register @apexl/icarus-* package components. Defaults to vendor
// useApi factory (no host wrapper installed yet).
registerBranding(app);
registerContentFlows(app);
registerAi(app);
registerPermissions(app);
registerContentLibrary(app);
registerTaxonomy(app);
registerViews(app);
registerNotifications(app);

const requireComponent = require.context(
    '@apexl/icarus-core/src/components', // The relative path of the components' folder
    true, // Whether to look in sub-folders
    /[A-Z]\w+\.(vue|js)$/ // The regular expression used to match base component filenames
);

const requireCustomComponent = require.context(
    './components', // The relative path of the components' folder
    true, // Whether to look in sub-folders
    /[A-Z]\w+\.(vue|js)$/ // The regular expression used to match base component filenames
);

// io-branding B.7 — build-time component theme tier. require.context's
// directory must be static, so this bundles every theme's files; we register
// only the ACTIVE theme's (ICARUS_THEME), which win over project + core.
const requireThemeComponent = require.context(
    './themes',
    true,
    /[A-Z]\w+\.(vue|js)$/
);

const toComponentName = fileName =>
    upperFirst(camelCase(fileName.split('/').pop().replace(/\.\w+$/, '')));

// Precedence: theme > project-custom > core. Each higher tier's names are
// skipped by the lower tiers so Vue never warns "already registered".
const activeTheme = (typeof ICARUS_THEME !== 'undefined' && ICARUS_THEME) ? ICARUS_THEME : '';
const themeKeys   = activeThemeKeys(requireThemeComponent.keys(), activeTheme);
const themeNames  = new Set(themeKeys.map(toComponentName));

const customNames = new Set(
    requireCustomComponent.keys().map(toComponentName)
);

// Theme (highest) — register the active theme's components first.
themeKeys.forEach(fileName => {
    const config = requireThemeComponent(fileName);
    app.component(toComponentName(fileName), config.default || config);
});

importComponents(app, requireCustomComponent, themeNames);                       // project, minus theme-claimed
importComponents(app, requireComponent, new Set([...themeNames, ...customNames])); // core, minus theme + project

function importComponents(app, components, skip = null) {
  components.keys().forEach(fileName => {
    const componentConfig = components(fileName); // Get component config

    // Get PascalCase name of component, Gets the file name regardless of folder depth
    const componentName = toComponentName(fileName);

    if (skip && skip.has(componentName)) {
      return; // a higher tier already claimed this name
    }

    // Register component globally
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    app.component(
        componentName,
        componentConfig.default || componentConfig
    )
  });
}

// Tablet/mobile LHS popout control. When the rail collapses to the icon strip
// (<=1290px, see _sidebar.scss), each section's submenu renders as a floating
// popout. Bootstrap toggles them independently, so manage them here:
//   - tap a section toggle  -> accordion: close every OTHER open popout
//   - tap a submenu link     -> a choice was made: close all popouts
//   - tap anywhere off the menu -> click-away dismiss: close all popouts
//   - tap inside a popout's whitespace -> leave it open
// All closes go through bootstrap's own API so they animate cleanly.
// Bubble phase: runs after bootstrap's data-api has toggled the clicked one.
document.addEventListener('click', (e) => {
  if (!window.matchMedia('(max-width: 1290px)').matches) return;

  const open = [...document.querySelectorAll('#leftSidebar .menu-group > ul.collapse.show')];
  if (!open.length) return;

  const hide = (ul) => Collapse.getOrCreateInstance(ul, { toggle: false }).hide();

  const toggle = e.target.closest('#leftSidebar .menu-group > a.dropdown-toggle');
  if (toggle) {
    const own = toggle.parentElement.querySelector(':scope > ul.collapse');
    open.forEach((ul) => { if (ul !== own) hide(ul); });
    return;
  }

  // Inside an open popout: only dismiss when a link (a choice) was clicked;
  // clicking the panel's own whitespace leaves it open.
  const panel = e.target.closest('#leftSidebar .menu-group > ul.collapse');
  if (panel && !e.target.closest('a')) return;

  open.forEach(hide);
});

// F-RN-03 — SPA catch-all 404. Registered LAST, deliberately after every
// other route touch-point above, per the audit finding's precedence
// instruction.
//
// @apexl/icarus-core's router (icarus/node_modules/@apexl/icarus-core/src/
// router/index.js) ships exactly ONE static route:
//   { path: '/:catchAll(.*)', name: 'All Requests', component: Icarus }
// Every URL in this app — including ones with no server-side page — matches
// that single wildcard and renders Icarus.vue, which then fetches the page
// tree for route.path from the backend. On an unmatched path the fetch
// fails, the failure is swallowed (usePageGetter.js only special-cases 403),
// and pageComponents/pageMetaData are simply never populated — the user is
// stranded on the boot-time placeholder title with a blank shell forever.
// There is no OTHER vue-router route anywhere in this app to "shadow" (no
// package calls router.addRoute either) — the entire route space is this
// one wildcard plus server-driven data, so a second `/:pathMatch(.*)*` added
// here would score identically to the vendor one and, per vue-router's
// insertion order for equal-score matchers, would never actually win a
// match. Wildcards can't out-rank each other; only a more specific (static)
// path can.
//
// So: the 404 is the STATIC /404.html page (src/web/404.html — branded,
// self-contained, copied to dist and served by the dev server's static
// config), reached via a FULL page load. A vue-router route can't work
// here: the sweep VERIFY pass proved App.vue has no <router-view> anywhere
// (it does its own manual page-tree fetch and ignores router matches
// entirely), so an in-app PageNotFound component is unrenderable dead code.
// A full-load redirect also fixes the vendor's own useApi().throw404()
// dangling `router.push('/404.html')` target: hard navigation to /404.html
// serves the static file (static files outrank the history fallback).
//
// The guard below is a conservative, purely client-side unknown-path check:
// Musubi's entire route space lives under a small, fixed set of top-level
// areas. A path whose first segment isn't one of them is unambiguously
// bogus — nothing in the app ever generates such a link. Anything under a
// known prefix still goes through the normal server-driven flow untouched,
// including deeper invalid paths under a valid prefix (e.g.
// /admin/deliverability) — catching those honestly requires the real
// route/menu data (the page-tree fetch inside Icarus.vue), not a guess from
// here. ⚠ Every REAL top-level section must be listed: the sweep verify
// pass caught /analytics missing, which blanked the whole Analytics area.
const KNOWN_TOP_LEVEL_PATHS = [
  '/', '/login', '/forgot-password', '/reset-password', '/reset-password-success',
  '/register', '/invite', '/profile', '/setup',
  '/admin', '/platform', '/outreach', '/acquire', '/analytics',
];
// Query-only navigations must be de-mangled BEFORE the unknown-path check.
//
// `router.push/replace({ query: … })` with no `path`/`name` is the idiomatic
// vue-router way to change a query param without leaving the page, and vendor
// components use it (io-content-library's ContentLibraryAdmin syncs its
// Blocks/Pricing tab that way). It resolves against the CURRENT matched
// record — and this app's entire route space is icarus-core's single
// `/:catchAll(.*)` wildcard, so vue-router re-stringifies the path out of the
// `catchAll` param and percent-encodes its slashes:
//
//   /admin/content-library  +  { query: { tab: 'pricing' } }
//     → /admin%2Fcontent-library?tab=pricing
//
// The guard below then read the first segment as `/admin%2Fcontent-library`,
// found no match, and full-loaded /404.html — which is exactly what the
// Content library "Pricing" tab did: one click, straight to the 404 page,
// without ever fetching a page tree. (Verified in dev against the live
// router: `router.resolve({ query })` returns the encoded path, while
// `router.resolve({ path: current.path, query })` returns the clean one.)
//
// Decoding here rather than in the vendor component fixes it once for every
// current and future query-only navigation, including ones in packages we
// don't own. Redirect (not just rewrite) so the address bar and the page-tree
// fetch in Icarus.vue both see the real path.
router.beforeEach((to) => {
  if (to.path.includes('%2F') || to.path.includes('%2f')) {
    return { path: decodeURIComponent(to.path), query: to.query, hash: to.hash, replace: true };
  }

  const firstSegment = '/' + (to.path.split('/')[1] || '');
  if (KNOWN_TOP_LEVEL_PATHS.includes(firstSegment)) return true;
  window.location.replace('/404.html'); // full load — the static branded page
  return false;
});

app.mount('#app');

// The shared @apexl/icarus-* packages other than icarus-core still write their
// icons as Font Awesome `<i class="fal fa-NAME">` markup, which nothing has
// drawn since the 3.1 icon migration. One document-wide observer, started
// once here, fills that markup with the Cygnus glyphs and labels icon-only
// buttons (see services/icons/legacyFaIcons.js).
startLegacyFaIcons();
